import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Archive,
  ArrowLeft,
  Fuel,
  Pencil,
  Plus,
  Printer,
  RotateCcw,
  Trash2,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import ManutenzioniAttrezzaturaDialog from "@/components/ManutenzioniAttrezzaturaDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import { printHtmlDirect } from "@/lib/directPrint";
import { openAttachmentViewer } from "@/lib/attachmentViewer";

const oggi = () => new Date().toISOString().slice(0, 10);
const empty = () => ({
  nome: "",
  marca: "",
  modello: "",
  targa: "",
  azienda: "",
  costo_orario: "",
  bollo: false,
  bollo_scadenza: "",
  assicurazione: false,
  assicurazione_scadenza: "",
  revisione: false,
  revisione_scadenza: "",
  libretto_omologazione: false,
  libretto_omologazione_scadenza: "",
});
const emptyCarico = () => ({
  data: oggi(),
  fornitore: "",
  litri: "",
  prezzo_litro: "",
});
const emptyScarico = () => ({
  data: oggi(),
  attrezzo_id: "",
  cantiere_id: "",
  litri: "",
});
const numberValue = (value) =>
  Number(String(value || 0).replace(",", ".")) || 0;
const escapeHtml = (value) => String(value ?? "")
  .replaceAll("&", "&amp;")
  .replaceAll("<", "&lt;")
  .replaceAll(">", "&gt;")
  .replaceAll('"', "&quot;")
  .replaceAll("'", "&#039;");

export default function AttrezzaturePage() {
  const navigate = useNavigate();
  const [sezione, setSezione] = useState("attrezzature");
  const [items, setItems] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [archivio, setArchivio] = useState(false);
  const [attrezziMinuti, setAttrezziMinuti] = useState([]);
  const [minutiArchivio, setMinutiArchivio] = useState(false);
  const [open, setOpen] = useState(false);
  const [maintenanceTool, setMaintenanceTool] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [bolloFile, setBolloFile] = useState(null);
  const [assicurazioneFile, setAssicurazioneFile] = useState(null);
  const [revisioneFile, setRevisioneFile] = useState(null);
  const [librettoOmologazioneFile, setLibrettoOmologazioneFile] = useState(null);
  const [gasolio, setGasolio] = useState({
    movimenti: [],
    giacenza: 0,
    prezzo_medio: 0,
    valore_giacenza: 0,
  });
  const [tipoMovimento, setTipoMovimento] = useState("carico");
  const [carico, setCarico] = useState(emptyCarico());
  const [scarico, setScarico] = useState(emptyScarico());

  const load = async () => {
    try {
      const [toolsRes, sitesRes, fuelRes, fornitoriRes, aziendeRes, minutiRes] =
        await Promise.all([
          api.get("/attrezzi-catalogo", {
            params: { include_archived: archivio },
          }),
          api.get("/cantieri"),
          api.get("/gasolio"),
          api.get("/gestionale/anagrafiche/fornitori", {
            params: { include_archived: false },
          }),
          api.get("/gestionale/anagrafiche/aziende", {
            params: { include_archived: false },
          }),
          api.get("/attrezzi-minuti", { params: { archivio: minutiArchivio } }),
        ]);
      const tools = Array.isArray(toolsRes.data) ? toolsRes.data : [];
      setItems(
        archivio
          ? tools.filter((x) => x.archived_at)
          : tools.filter((x) => !x.archived_at),
      );
      const sites = Array.isArray(sitesRes.data) ? sitesRes.data : [];
      setCantieri(
        sites.filter((x) => (x.stato || "").toLowerCase() !== "completato"),
      );
      const anagrafiche = [
        ...(Array.isArray(fornitoriRes.data) ? fornitoriRes.data : []),
        ...(Array.isArray(aziendeRes.data) ? aziendeRes.data : []),
      ].filter((x) => !x.archived_at && x.nome);
      setFornitori(
        [
          ...new Set(anagrafiche.map((x) => x.nome.trim()).filter(Boolean)),
        ].sort((a, b) => a.localeCompare(b, "it")),
      );
      setAziende(
        (Array.isArray(aziendeRes.data) ? aziendeRes.data : [])
          .filter((x) => !x.archived_at && x.nome)
          .map((x) => x.nome.trim())
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b, "it")),
      );
      setGasolio(fuelRes.data || {});
      setAttrezziMinuti(Array.isArray(minutiRes.data) ? minutiRes.data : []);
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
  }, [archivio, minutiArchivio]);
  const activeTools = useMemo(
    () => items.filter((x) => !x.archived_at),
    [items],
  );
  const nuovo = () => {
    setEditing(null);
    setForm(empty());
    setBolloFile(null);
    setAssicurazioneFile(null);
    setRevisioneFile(null);
    setLibrettoOmologazioneFile(null);
    setOpen(true);
  };
  const modifica = (item) => {
    setEditing(item);
    setForm({
      ...empty(),
      ...item,
      costo_orario: String(item.costo_orario ?? ""),
    });
    setBolloFile(null);
    setAssicurazioneFile(null);
    setRevisioneFile(null);
    setLibrettoOmologazioneFile(null);
    setOpen(true);
  };
  const save = async (event) => {
    event.preventDefault();
    try {
      const payload = { ...form, costo_orario: numberValue(form.costo_orario) };
      const response = editing
        ? await api.put(`/attrezzi-catalogo/${editing.id}`, payload)
        : await api.post("/attrezzi-catalogo", payload);
      const id = editing?.id || response.data.id;
      for (const [tipo, file] of [
        ["bollo", bolloFile],
        ["assicurazione", assicurazioneFile],
        ["revisione", revisioneFile],
        ["libretto_omologazione", librettoOmologazioneFile],
      ]) {
        if (!file) continue;
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/attrezzi-catalogo/${id}/documenti/${tipo}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Attrezzatura salvata");
      setOpen(false);
      setEditing(null);
      load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };
  const eliminaDocumento = async (tipo) => {
    if (!editing || !window.confirm("Eliminare il documento e la relativa scadenza?")) return;
    try {
      await api.delete(`/attrezzi-catalogo/${editing.id}/documenti/${tipo}`);
      setEditing((corrente) => ({ ...corrente, [tipo]: false, [`${tipo}_scadenza`]: "", [`${tipo}_file_path`]: null, [`${tipo}_file_nome`]: null }));
      setForm((corrente) => ({ ...corrente, [tipo]: false, [`${tipo}_scadenza`]: "" }));
      if (tipo === "bollo") setBolloFile(null);
      else if (tipo === "assicurazione") setAssicurazioneFile(null);
      else if (tipo === "revisione") setRevisioneFile(null);
      else setLibrettoOmologazioneFile(null);
      toast.success("Documento e scadenza eliminati");
      load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };
  const archivia = async (item) => {
    if (!window.confirm(`Archiviare "${item.nome}"?`)) return;
    try {
      await api.delete(`/attrezzi-catalogo/${item.id}`);
      setOpen(false);
      load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };
  const ripristina = async (item) => {
    try {
      await api.post(`/attrezzi-catalogo/${item.id}/restore`);
      load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };
  const salvaCarico = async (event) => {
    event.preventDefault();
    try {
      await api.post("/gasolio/carichi", {
        ...carico,
        litri: numberValue(carico.litri),
        prezzo_litro: numberValue(carico.prezzo_litro),
      });
      toast.success("Carico gasolio registrato");
      setCarico(emptyCarico());
      load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };
  const salvaScarico = async (event) => {
    event.preventDefault();
    try {
      await api.post("/gasolio/scarichi", {
        ...scarico,
        litri: numberValue(scarico.litri),
      });
      toast.success(
        "Scarico registrato nel cantiere, nei Materiali e nei registri",
      );
      setScarico(emptyScarico());
      load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };
  const eliminaMovimento = async (item) => {
    if (
      !window.confirm(
        "Eliminare questo movimento? I collegamenti saranno aggiornati automaticamente.",
      )
    )
      return;
    try {
      await api.delete(`/gasolio/movimenti/${item.id}`);
      toast.success("Movimento eliminato");
      load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const cambiaStatoMinuto = async (item, stato) => {
    const etichetta = stato === "venduto" ? "venduto" : "dismesso";
    if (!window.confirm(`Segnare “${item.nome}” come ${etichetta}? Rimarrà consultabile nell'archivio.`)) return;
    try {
      await api.put(`/attrezzi-minuti/${item.id}/stato`, { stato });
      toast.success(`Attrezzo segnato come ${etichetta}`);
      load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const ripristinaAttrezzoMinuto = async (item) => {
    if (!window.confirm(`Ripristinare “${item.nome}” tra gli attrezzi attivi?`)) return;
    try {
      await api.post(`/attrezzi-minuti/${item.id}/restore`);
      toast.success("Attrezzo ripristinato");
      load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const eliminaDefinitivamenteAttrezzoMinuto = async (item) => {
    if (!window.confirm(`Eliminare definitivamente “${item.nome}”? Questa operazione non può essere annullata.`)) return;
    try {
      await api.delete(`/attrezzi-minuti/${item.id}/permanent`);
      toast.success("Attrezzo eliminato definitivamente");
      load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  const stampaAttrezziMinuti = () => {
    const titolo = minutiArchivio ? "Archivio attrezzi minuti" : "Attrezzi minuti";
    const totale = attrezziMinuti.reduce((somma, item) => somma + numberValue(item.importo), 0);
    const righe = attrezziMinuti.map((item) => {
      const data = item.data ? new Date(`${item.data}T12:00:00`).toLocaleDateString("it-IT") : "-";
      const stato = item.stato === "venduto" ? "Venduto" : item.stato === "dismesso" ? "Dismesso" : "Attivo";
      return `<tr><td>${escapeHtml(data)}</td><td>${escapeHtml(item.nome || "-")}</td><td>${escapeHtml(item.fornitore || "-")}</td><td>${escapeHtml(item.numero_documento || "-")}</td><td>${escapeHtml(item.azienda_nome || "-")}</td><td class="numero">${escapeHtml(eur(item.importo))}</td><td>${escapeHtml(stato)}</td></tr>`;
    }).join("");
    printHtmlDirect(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(titolo)}</title><style>@page{size:A4 landscape;margin:12mm}body{font-family:Arial,sans-serif;color:#172033}h1{margin:0 0 6px;font-size:24px}p{margin:0 0 18px;color:#64748b}table{width:100%;border-collapse:collapse;font-size:11px}th,td{border:1px solid #cbd5e1;padding:7px;text-align:left}th{background:#eef2f6}.numero{text-align:right;white-space:nowrap}tfoot td{font-weight:bold;background:#f8fafc}</style></head><body><h1>${escapeHtml(titolo)}</h1><p>Report del ${escapeHtml(new Date().toLocaleDateString("it-IT"))}</p><table><thead><tr><th>Data</th><th>Attrezzatura</th><th>Fornitore</th><th>Numero fattura</th><th>Azienda</th><th>Imponibile</th><th>Stato</th></tr></thead><tbody>${righe || '<tr><td colspan="7">Nessun dato presente</td></tr>'}</tbody><tfoot><tr><td colspan="5">Totale (${attrezziMinuti.length} righe)</td><td class="numero">${escapeHtml(eur(totale))}</td><td></td></tr></tfoot></table></body></html>`, titolo);
  };

  return (
    <AppShell>
      <button
        onClick={() => navigate("/gestionale")}
        className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary"
      >
        <ArrowLeft className="h-4 w-4" />
        Gestionale
      </button>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[.2em] text-muted-foreground">
            Gestionale
          </p>
          <h1 className="mt-2 font-heading text-4xl font-bold">Attrezzature</h1>
        </div>
        <div className="flex gap-2">
          <Button
            variant={sezione === "attrezzature" ? "default" : "outline"}
            onClick={() => setSezione("attrezzature")}
          >
            Attrezzature
          </Button>
          <Button
            variant={sezione === "minuti" ? "default" : "outline"}
            onClick={() => setSezione("minuti")}
          >
            <FileText className="mr-2 h-4 w-4" />
            Attrezzi minuti
          </Button>
          <Button
            variant={sezione === "gasolio" ? "default" : "outline"}
            onClick={() => {
              setArchivio(false);
              setSezione("gasolio");
            }}
          >
            <Fuel className="mr-2 h-4 w-4" />
            Gasolio
          </Button>
        </div>
      </div>

      {sezione === "attrezzature" ? (
        <>
          <div className="mb-5 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setArchivio((v) => !v)}>
              <Archive className="mr-2 h-4 w-4" />
              {archivio ? "Attrezzature attive" : "Archivio"}
            </Button>
            {!archivio && (
              <Button onClick={nuovo}>
                <Plus className="mr-2 h-4 w-4" />
                Nuova attrezzatura
              </Button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="rounded-xl border bg-card p-10 text-center text-muted-foreground">
              {archivio
                ? "Nessuna attrezzatura archiviata"
                : "Nessuna attrezzatura inserita"}
            </div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between gap-4 rounded-xl border bg-card p-5"
                >
                  <div>
                    <p className="font-bold">{item.nome}</p>
                    <p className="text-sm text-muted-foreground">
                      {[item.marca, item.modello, item.targa]
                        .filter(Boolean)
                        .join(" - ") || "-"}
                    </p>
                    <p className="mt-2 text-sm">
                      {item.azienda || "Azienda non indicata"} -{" "}
                      {eur(item.costo_orario)}/h
                    </p>
                  </div>
                  <div>
                    {item.archived_at ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => ripristina(item)}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          onClick={() => setMaintenanceTool(item)}
                        >
                          Manutenzioni
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => modifica(item)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => archivia(item)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : sezione === "minuti" ? (
        <>
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div><h2 className="font-heading text-2xl font-bold">Attrezzi minuti</h2><p className="text-sm text-muted-foreground">Acquisti generati automaticamente dalle fatture registrate come Attrezzatura.</p></div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={stampaAttrezziMinuti}><Printer className="mr-2 h-4 w-4" />Stampa PDF</Button>
              <Button variant="outline" onClick={() => setMinutiArchivio((value) => !value)}><Archive className="mr-2 h-4 w-4" />{minutiArchivio ? "Attrezzi attivi" : "Archivio"}</Button>
            </div>
          </div>
          <div className="overflow-auto rounded-xl border bg-card max-h-[600px]">
            <table className="w-full min-w-[980px] text-sm">
              <thead className="sticky top-0 z-10 bg-muted"><tr className="text-left"><th className="p-3">Data</th><th className="p-3">Attrezzatura</th><th className="p-3">Fornitore</th><th className="p-3">Numero fattura</th><th className="p-3">Azienda</th><th className="p-3 text-right">Imponibile</th><th className="p-3">Stato</th><th className="p-3">Azioni</th></tr></thead>
              <tbody>{attrezziMinuti.length === 0 ? <tr><td colSpan="8" className="p-10 text-center text-muted-foreground">{minutiArchivio ? "Nessun attrezzo venduto o dismesso" : "Nessun attrezzo minuto presente"}</td></tr> : attrezziMinuti.map((item) => <tr key={item.id} className="border-t"><td className="p-3 whitespace-nowrap">{item.data ? new Date(`${item.data}T12:00:00`).toLocaleDateString("it-IT") : "-"}</td><td className="p-3 font-semibold">{item.nome}</td><td className="p-3">{item.fornitore || "-"}</td><td className="p-3">{item.numero_documento || "-"}</td><td className="p-3">{item.azienda_nome || "-"}</td><td className="p-3 text-right font-semibold">{eur(item.importo)}</td><td className="p-3"><span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${item.stato === "venduto" ? "bg-blue-50 text-blue-700" : item.stato === "dismesso" ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>{item.stato === "venduto" ? "Venduto" : item.stato === "dismesso" ? "Dismesso" : "Attivo"}</span></td><td className="p-3">{minutiArchivio ? <div className="flex gap-1"><Button size="sm" variant="outline" onClick={() => ripristinaAttrezzoMinuto(item)}><RotateCcw className="mr-1 h-4 w-4" />Ripristina</Button><Button size="sm" variant="ghost" className="text-destructive" onClick={() => eliminaDefinitivamenteAttrezzoMinuto(item)}><Trash2 className="mr-1 h-4 w-4" />Elimina</Button></div> : <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => cambiaStatoMinuto(item, "venduto")}>Venduto</Button><Button size="sm" variant="outline" onClick={() => cambiaStatoMinuto(item, "dismesso")}>Dismesso</Button></div>}</td></tr>)}</tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div className="mb-5 grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl border bg-card p-5">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Giacenza
              </p>
              <p className="mt-2 text-3xl font-bold">
                {numberValue(gasolio.giacenza).toLocaleString("it-IT", {
                  maximumFractionDigits: 3,
                })}{" "}
                L
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Costo ultimo carico
              </p>
              <p className="mt-2 text-3xl font-bold">
                {eur(gasolio.prezzo_ultimo_carico ?? gasolio.prezzo_medio)}/L
              </p>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Valore giacenza
              </p>
              <p className="mt-2 text-3xl font-bold">
                {eur(gasolio.valore_giacenza)}
              </p>
            </div>
          </div>
          <div className="grid gap-5 lg:grid-cols-[380px_1fr] lg:min-h-0">
            <div className="rounded-xl border bg-card p-5">
              <div className="mb-5 grid grid-cols-2 rounded-lg bg-muted p-1">
                <Button
                  variant={tipoMovimento === "carico" ? "default" : "ghost"}
                  onClick={() => setTipoMovimento("carico")}
                >
                  Carico
                </Button>
                <Button
                  variant={tipoMovimento === "scarico" ? "default" : "ghost"}
                  onClick={() => setTipoMovimento("scarico")}
                >
                  Scarico
                </Button>
              </div>
              {tipoMovimento === "carico" ? (
                <form onSubmit={salvaCarico} className="space-y-4">
                  <div>
                    <Label>Data *</Label>
                    <Input
                      type="date"
                      value={carico.data}
                      onChange={(e) =>
                        setCarico({ ...carico, data: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label>Fornitore *</Label>
                    <SearchableCombobox
                      value={carico.fornitore}
                      onValueChange={(value) => setCarico({ ...carico, fornitore: value })}
                      options={fornitori.map((nome) => ({ value: nome, label: nome }))}
                      placeholder="Seleziona azienda o fornitore"
                      searchPlaceholder="Scrivi il fornitore..."
                    />
                    {fornitori.length === 0 && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Inserisci prima aziende o fornitori nella sezione
                        Aziende e parametri.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Litri *</Label>
                    <Input
                      type="number"
                      min="0.001"
                      step="0.001"
                      value={carico.litri}
                      onChange={(e) =>
                        setCarico({ ...carico, litri: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label>Prezzo EUR/litro *</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.0001"
                      value={carico.prezzo_litro}
                      onChange={(e) =>
                        setCarico({ ...carico, prezzo_litro: e.target.value })
                      }
                      required
                    />
                  </div>
                  <Button className="w-full" type="submit">
                    Registra carico
                  </Button>
                </form>
              ) : (
                <form onSubmit={salvaScarico} className="space-y-4">
                  <div>
                    <Label>Data *</Label>
                    <Input
                      type="date"
                      value={scarico.data}
                      onChange={(e) =>
                        setScarico({ ...scarico, data: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label>Attrezzatura *</Label>
                    <Select
                      value={scarico.attrezzo_id || "__vuoto__"}
                      onValueChange={(value) =>
                        setScarico({
                          ...scarico,
                          attrezzo_id: value === "__vuoto__" ? "" : value,
                        })
                      }
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Seleziona attrezzatura" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__vuoto__">
                          Seleziona attrezzatura
                        </SelectItem>
                        {activeTools.map((x) => (
                          <SelectItem key={x.id} value={x.id}>
                            {x.nome}
                            {x.targa ? ` - ${x.targa}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Cantiere *</Label>
                    <SearchableCombobox
                      value={scarico.cantiere_id}
                      onValueChange={(value) => setScarico({ ...scarico, cantiere_id: value })}
                      options={cantieri.map((x) => ({ value: x.id, label: x.nome }))}
                      placeholder="Seleziona cantiere"
                      searchPlaceholder="Scrivi il cantiere..."
                    />
                  </div>
                  <div>
                    <Label>Litri *</Label>
                    <Input
                      type="number"
                      min="0.001"
                      step="0.001"
                      value={scarico.litri}
                      onChange={(e) =>
                        setScarico({ ...scarico, litri: e.target.value })
                      }
                      required
                    />
                  </div>
                  <Button className="w-full" type="submit">
                    Registra scarico
                  </Button>
                </form>
              )}
            </div>
            <div className="rounded-xl border bg-card overflow-auto max-h-[560px]">
              <table className="w-full min-w-[850px] text-sm">
                <thead className="sticky top-0 z-10 bg-muted">
                  <tr className="text-left">
                    <th className="p-3">Data</th>
                    <th className="p-3">Tipo</th>
                    <th className="p-3">Fornitore / Attrezzatura</th>
                    <th className="p-3">Cantiere</th>
                    <th className="p-3 text-right">Litri</th>
                    <th className="p-3 text-right">EUR/litro</th>
                    <th className="p-3 text-right">Totale</th>
                    <th className="p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {(gasolio.movimenti || []).length === 0 ? (
                    <tr>
                      <td
                        colSpan="8"
                        className="p-10 text-center text-muted-foreground"
                      >
                        Nessun movimento registrato
                      </td>
                    </tr>
                  ) : (
                    (gasolio.movimenti || []).map((x) => (
                      <tr key={x.id} className="border-t">
                        <td className="p-3">
                          {x.data?.split("-").reverse().join("/")}
                        </td>
                        <td className="p-3">
                          <span
                            className={
                              x.tipo === "carico"
                                ? "text-emerald-700"
                                : "text-orange-700"
                            }
                          >
                            {x.tipo === "carico" ? "Carico" : "Scarico"}
                          </span>
                        </td>
                        <td className="p-3">
                          {x.tipo === "carico" ? x.fornitore : x.attrezzo_nome}
                        </td>
                        <td className="p-3">{x.cantiere_nome || "-"}</td>
                        <td className="p-3 text-right">
                          {numberValue(x.litri).toLocaleString("it-IT", {
                            maximumFractionDigits: 3,
                          })}
                        </td>
                        <td className="p-3 text-right">
                          {eur(x.prezzo_litro)}
                        </td>
                        <td className="p-3 text-right font-semibold">
                          {eur(x.totale)}
                        </td>
                        <td className="p-3">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => eliminaMovimento(x)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {maintenanceTool && (
        <ManutenzioniAttrezzaturaDialog
          attrezzatura={maintenanceTool}
          onClose={() => setMaintenanceTool(null)}
        />
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Modifica attrezzatura" : "Nuova attrezzatura"}
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={save}
            className="grid grid-cols-1 gap-3 sm:grid-cols-2"
          >
            {[
              ["Nome", "nome"],
              ["Marca", "marca"],
              ["Modello", "modello"],
              ["Targa", "targa"],
              ["Costo orario", "costo_orario"],
            ].map(([label, key]) => (
              <div key={key}>
                <Label>{label}</Label>
                <Input
                  type={key === "costo_orario" ? "number" : "text"}
                  step={key === "costo_orario" ? "0.01" : undefined}
                  value={form[key] || ""}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  required={key === "nome"}
                />
              </div>
            ))}
            <div>
              <Label>Azienda</Label>
              <Select
                value={form.azienda || "__vuoto__"}
                onValueChange={(value) =>
                  setForm({
                    ...form,
                    azienda: value === "__vuoto__" ? "" : value,
                  })
                }
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Nessuna azienda" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__vuoto__">Nessuna azienda</SelectItem>
                  {form.azienda && !aziende.includes(form.azienda) && (
                    <SelectItem value={form.azienda}>
                      {form.azienda} (valore storico)
                    </SelectItem>
                  )}
                  {aziende.map((nome) => (
                    <SelectItem key={nome} value={nome}>
                      {nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {["bollo", "assicurazione", "revisione", "libretto_omologazione"].map((tipo) => {
              const titolo =
                tipo === "bollo"
                  ? "Bollo"
                  : tipo === "assicurazione"
                    ? "Assicurazione"
                    : tipo === "revisione"
                      ? "Revisione"
                      : "Libretto / Omologazione";
              const fileEsistente = editing?.[`${tipo}_file_nome`];
              return (
                <div
                  key={tipo}
                  className="sm:col-span-2 space-y-3 rounded-xl border p-4"
                >
                  <label className="flex items-center gap-2 font-semibold">
                    <input
                      type="checkbox"
                      checked={Boolean(form[tipo])}
                      onChange={(e) =>
                        setForm({ ...form, [tipo]: e.target.checked })
                      }
                    />
                    {titolo}
                  </label>
                  {form[tipo] && (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <Label>Data di scadenza</Label>
                        <Input
                          type="date"
                          value={form[`${tipo}_scadenza`] || ""}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              [`${tipo}_scadenza`]: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <Label>Documento collegato</Label>
                        <Input
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif,.gif"
                          onChange={(e) =>
                            tipo === "bollo"
                              ? setBolloFile(e.target.files?.[0] || null)
                              : tipo === "assicurazione"
                                ? setAssicurazioneFile(
                                    e.target.files?.[0] || null,
                                  )
                                : tipo === "revisione"
                                  ? setRevisioneFile(e.target.files?.[0] || null)
                                  : setLibrettoOmologazioneFile(e.target.files?.[0] || null)
                          }
                        />
                      </div>
                    </div>
                  )}
                  {fileEsistente && (
                    <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-2">
                      <button type="button" className="inline-flex min-w-0 items-center gap-2 text-sm font-semibold text-primary hover:underline" onClick={() => openAttachmentViewer(`${api.defaults.baseURL}/attrezzi-catalogo/${editing.id}/documenti/${tipo}/file`, fileEsistente)}>
                        <FileText className="h-4 w-4 shrink-0" /><span className="truncate">{fileEsistente}</span>
                      </button>
                      <Button type="button" size="icon" variant="ghost" className="shrink-0 text-destructive hover:text-destructive" title={`Elimina ${titolo.toLowerCase()} e scadenza`} onClick={() => eliminaDocumento(tipo)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
            <DialogFooter className="sm:col-span-2">
              {editing && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => archivia(editing)}
                >
                  <Archive className="mr-2 h-4 w-4" />
                  Archivia attrezzatura
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Annulla
              </Button>
              <Button type="submit">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
